import { useEffect, useRef, useState } from "react";
import { isGoogleMapsConfigured, loadGoogleMapsPlaces } from "../../utils/googleMapsLoader";
import { reverseGeocodeAddress } from "../../utils/reverseGeocode";

/**
 * Patient-location field for the Emergency Request form. Always works as a
 * plain text input + "use current location" (free, no API key, via the
 * existing Nominatim reverseGeocodeAddress utility). When
 * REACT_APP_GOOGLE_MAPS_API_KEY is set, typed input also gets real Google
 * Places autocomplete layered on top — see googleMapsLoader.js.
 */
export default function EmergencyRequestAddressField({ value, onChange, onCoordsChange, disabled }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState("");

  useEffect(() => {
    if (!isGoogleMapsConfigured()) return undefined;
    let cancelled = false;
    loadGoogleMapsPlaces()
      .then((google) => {
        if (cancelled || !google || !inputRef.current || autocompleteRef.current) return;
        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: "in" },
          fields: ["formatted_address", "geometry", "name"],
        });
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();
          const address = place?.formatted_address || place?.name || "";
          if (address) onChange(address);
          const lat = typeof place?.geometry?.location?.lat === "function" ? place.geometry.location.lat() : null;
          const lng = typeof place?.geometry?.location?.lng === "function" ? place.geometry.location.lng() : null;
          if (typeof lat === "number" && typeof lng === "number") onCoordsChange(lat, lng);
        });
      })
      .catch(() => {
        /* Places failed to load — the plain text input still works fine. */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function useCurrentLocation() {
    setLocateError("");
    if (!navigator.geolocation) {
      setLocateError("Location access is not available in this browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        onCoordsChange(latitude, longitude);
        const resolved = await reverseGeocodeAddress({ latitude, longitude });
        setLocating(false);
        onChange(resolved?.address || `Pinned location (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`);
      },
      () => {
        setLocating(false);
        setLocateError("Allow location access, or type the address manually.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }

  return (
    <div className="emreq-field emreq-field--address">
      <label htmlFor="emreq-address">Patient Location *</label>
      <div className="emreq-address-row">
        <input
          id="emreq-address"
          ref={inputRef}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="House / street / area, city"
          disabled={disabled}
          autoComplete="off"
          required
        />
        <button
          type="button"
          className="emreq-locate-btn"
          onClick={useCurrentLocation}
          disabled={disabled || locating}
        >
          {locating ? "Locating…" : "📍 Use current location"}
        </button>
      </div>
      {locateError ? <p className="emreq-field__error">{locateError}</p> : null}
    </div>
  );
}
